/**
 * Dispatch one ActivationAction by routing class (contracts §ACTION_ROUTING):
 *   P / C → onNavigate(route)        D → openDialog(dialog, dialogProps)
 *                                        (fallbackRoute when no host, or when the host
 *                                         returns false for that dialog kind)
 *   X     → openExternal(externalUrl) (defaults to window.open with noopener)
 * Router-free: everything is injected (DC-9) — no react-router import, no direct location reads.
 */
import type { ActivationAction } from '@modules/shared-types';

export interface ActivationActionContext {
  onNavigate: (path: string) => void;
  /**
   * Opens a class-D dialog. Additive (follow-up F1): a host that does NOT own the
   * requested dialog kind returns `false`, and the dispatcher falls back to the item's
   * `fallbackRoute` — exactly as when no host is mounted at all. Returning nothing
   * (the pre-F1 contract) still means "handled".
   */
  openDialog: (dialog: NonNullable<ActivationAction['dialog']>, props?: Record<string, string>) => boolean | void;
  openExternal?: (url: string) => void;
}

const defaultOpenExternal = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function runActivationAction(action: ActivationAction, ctx: ActivationActionContext): void {
  switch (action.class) {
    case 'P':
    case 'C':
      if (action.route) ctx.onNavigate(action.route);
      return;
    case 'D': {
      if (action.dialog && typeof ctx.openDialog === 'function') {
        const handled = ctx.openDialog(action.dialog, action.dialogProps);
        if (handled !== false) return;
      }
      if (action.fallbackRoute) ctx.onNavigate(action.fallbackRoute);
      return;
    }
    case 'X':
      if (action.externalUrl) (ctx.openExternal ?? defaultOpenExternal)(action.externalUrl);
      return;
    default:
      return;
  }
}

/** The url param the published questionnaire binds the VISITOR's project id from (D-I4-1). */
export const QUESTIONNAIRE_PROJECT_PARAM = 'sourceProjectId';

/**
 * Sets `sourceProjectId` on the published intent questionnaire URL (VITE_LAUNCH_INTENT_FUNNEL_URL).
 *
 * D-I4-1: the param is deliberately NOT `projectId` — on a default-domain host the edge gateway
 * consumes `?projectId=` itself to pick the published funnel's GCS path
 * (`web-v2/<projectId>/<alias>/index.html`), so a link carrying the visitor's project id under that
 * name answers 410 "This funnel was unpublished" for every project except the questionnaire's owner.
 *
 * I2: this must OVERWRITE, never append. The web-shell reads the FIRST occurrence of the param, so a
 * configured URL that already carries one (e.g. a project id baked in by
 * `scripts/launch-activation/apply-intent-questionnaire.sh`) would silently write every visitor's
 * intent to that project. `searchParams.set` replaces every existing occurrence in place.
 */
export function questionnaireLink(questionnaireUrl: string, projectId: string): string {
  const RELATIVE_BASE = 'https://relative.invalid';
  let url: URL;
  let absolute = true;
  try {
    url = new URL(questionnaireUrl);
  } catch {
    try {
      url = new URL(questionnaireUrl, RELATIVE_BASE);
      absolute = false;
    } catch {
      return questionnaireUrl;
    }
  }
  url.searchParams.set(QUESTIONNAIRE_PROJECT_PARAM, projectId);
  return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}
